// NextActionWidget
//
// Picks the single most useful next step for a firm based on what they
// already have in the database, and shows a focused "do this next" card on
// the dashboard. The rule is: surface ONE action at a time. A laundry list
// of suggestions is paralysing; one concrete step is actionable.
//
// Priority ladder (top to bottom — first matching rule wins):
//   1. No contacts                   → Add your first client
//   2. Contacts but no matters       → Open a case file
//   3. Matters but no time entries   → Log time
//   4. Time but no invoices          → Send an invoice
//   5. Invoices but no payments      → Record a payment
//   6. Solo (just owner, no team)    → Invite a teammate
//   7. Matters but no portal invite  → Invite client to the portal
//   8. Otherwise                     → don't render
//
// The widget self-hides if the user has been dismissed it for the session,
// or if no rule matches.

import React, { useMemo, useState } from 'react';
import {
  UserPlus, Briefcase, Clock, Receipt, Banknote, Users2, Share2,
  ArrowRight, X, Lightbulb,
} from 'lucide-react';
import { useRoute } from '../../lib/router';
import { Card } from '../ui';

const DISMISS_KEY = 'lf_next_action_dismissed_session_v1';

function buildSuggestion(state, navigate) {
  const {
    contactsCount, mattersCount, timeEntriesCount, invoicesCount,
    paymentsCount, teamCount, anyPortalLinked,
  } = state;

  if (contactsCount === 0) {
    return {
      id:    'add_contact',
      icon:  UserPlus,
      tone:  'indigo',
      title: 'Add your first client',
      body:  'Every case file belongs to a client. Add one to start tracking matters, time and invoices for them.',
      cta:   'Add a contact',
      onClick: () => navigate('/contacts'),
    };
  }

  if (mattersCount === 0) {
    return {
      id:    'open_matter',
      icon:  Briefcase,
      tone:  'indigo',
      title: 'Open your first case file',
      body:  'Case files hold everything for a single matter — deadlines, time, documents, billing and client messages all in one place.',
      cta:   'New case file',
      onClick: () => navigate('/matters'),
    };
  }

  if (timeEntriesCount === 0) {
    return {
      id:    'log_time',
      icon:  Clock,
      tone:  'emerald',
      title: 'Log time on a matter',
      body:  'Even on flat-fee matters, tracking time tells you whether the fee is profitable. A few seconds now, big picture later.',
      cta:   'Log time',
      onClick: () => navigate('/time'),
    };
  }

  if (invoicesCount === 0) {
    return {
      id:    'send_invoice',
      icon:  Receipt,
      tone:  'amber',
      title: 'Send your first invoice',
      body:  'Bill your client for the work you\'ve logged. DocketFlow pulls in time entries and case charges automatically.',
      cta:   'Create an invoice',
      onClick: () => navigate('/billing'),
    };
  }

  if (paymentsCount === 0) {
    return {
      id:    'record_payment',
      icon:  Banknote,
      tone:  'emerald',
      title: 'Record a payment',
      body:  'When the client pays, record the payment here so your invoice clears and the receipt is filed under their name.',
      cta:   'Record payment',
      onClick: () => navigate('/billing'),
    };
  }

  if (teamCount <= 1) {
    return {
      id:    'invite_team',
      icon:  Users2,
      tone:  'indigo',
      title: 'Invite a teammate',
      body:  'Most firms run with at least two people. Invite an associate, paralegal or partner — they get their own login and role-based access.',
      cta:   'Go to settings',
      onClick: () => navigate('/settings'),
    };
  }

  if (mattersCount > 0 && !anyPortalLinked) {
    return {
      id:    'invite_client',
      icon:  Share2,
      tone:  'fuchsia',
      title: 'Give a client portal access',
      body:  'Clients can log into their own portal to see case status, download invoices and receipts, and exchange documents with you securely.',
      cta:   'Open contacts',
      onClick: () => navigate('/contacts'),
    };
  }

  return null;
}

export default function NextActionWidget({
  contacts = [], matters = [], timeEntries = [], invoices = [], payments = [],
  teamCount = 1,
}) {
  const [, navigate] = useRoute();
  const [dismissed, setDismissed] = useState(() => {
    try { return !!sessionStorage.getItem(DISMISS_KEY); } catch { return false; }
  });

  const suggestion = useMemo(
    () => buildSuggestion({
      contactsCount:    contacts.length,
      mattersCount:     matters.length,
      timeEntriesCount: timeEntries.length,
      invoicesCount:    invoices.length,
      paymentsCount:    payments.length,
      teamCount,
      anyPortalLinked:  contacts.some((c) => !!c.portal_user_id),
    }, navigate),
    [contacts, matters, timeEntries, invoices, payments, teamCount, navigate],
  );

  if (dismissed || !suggestion) return null;

  const TONES = {
    indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-900/15',  ring: 'border-indigo-200 dark:border-indigo-800',  icon: 'bg-indigo-600',  cta: 'bg-indigo-600 hover:bg-indigo-700' },
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/15', ring: 'border-emerald-200 dark:border-emerald-800', icon: 'bg-emerald-600', cta: 'bg-emerald-600 hover:bg-emerald-700' },
    amber:   { bg: 'bg-amber-50 dark:bg-amber-900/15',     ring: 'border-amber-200 dark:border-amber-800',     icon: 'bg-amber-600',   cta: 'bg-amber-600 hover:bg-amber-700' },
    fuchsia: { bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/15', ring: 'border-fuchsia-200 dark:border-fuchsia-800', icon: 'bg-fuchsia-600', cta: 'bg-fuchsia-600 hover:bg-fuchsia-700' },
  };
  const tone = TONES[suggestion.tone] || TONES.indigo;

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const Icon = suggestion.icon;
  return (
    <Card className={`relative overflow-hidden ${tone.bg} border ${tone.ring}`}>
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100/60 dark:hover:bg-slate-800/60 transition"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div className="p-5 flex items-start gap-4">
        <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${tone.icon}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-1.5 mb-1">
            <Lightbulb size={11} className="text-amber-500" />
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
              Suggested next step
            </p>
          </div>
          <p className="text-base font-semibold text-slate-900 dark:text-white">{suggestion.title}</p>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{suggestion.body}</p>
          <button
            onClick={suggestion.onClick}
            className={`mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium transition ${tone.cta}`}
          >
            {suggestion.cta} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </Card>
  );
}
